import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import * as Sentry from "@sentry/capacitor";
import { apiFetch } from "@/lib/api-fetch";
import { blobToBase64 } from "@/lib/blob";

/** The server names the file; a header the browser hides leaves the fallback. */
const FILENAME_RE = /filename="(?<filename>[^"]+)"/u;

/** What the Share plugin rejects with when the host closes the sheet. */
const SHARE_CANCELED = "Share canceled";

/** Where a shared file waits for the share sheet, in the app's own cache. */
const EXPORTS = { directory: Directory.Cache, path: "exports" };

/** The Filesystem plugin's code, on both platforms, for a path not there. */
const FOLDER_NOT_FOUND = "OS-PLUG-FILE-0008";

/** FileSaver.js's wait: WebKit can start a blob download after the click task. */
const REVOKE_AFTER_MS = 40_000;

/**
 * Fetch a file the API serves as an attachment and hand it to the host to
 * keep, under the name the server gave it. Throws when the API refuses.
 */
export async function downloadApiFile(
  path: string,
  fallbackName: string
): Promise<void> {
  const res = await apiFetch(path);
  if (!res.ok) {
    throw new Error(`download of ${path} failed (${res.status})`);
  }
  const name =
    FILENAME_RE.exec(res.headers.get("Content-Disposition") ?? "")?.groups
      ?.filename ?? fallbackName;
  const blob = await res.blob();
  if (Capacitor.isNativePlatform()) {
    await shareFile(name, blob);
  } else {
    saveInBrowser(name, blob);
  }
}

/**
 * Delete what the app shells shared out, with the account that shared it: a
 * registry export holds guests' documents. Not when a sheet closes, since
 * Android settles it before the target has read the file. Never throws: a
 * failure is reported if the page outlives it, and the folder goes with the
 * next completed sign-out.
 */
export async function clearSharedFiles(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await Filesystem.rmdir({ ...EXPORTS, recursive: true });
  } catch (error) {
    if ((error as { code?: string }).code !== FOLDER_NOT_FOUND) {
      Sentry.captureException(error);
    }
  }
}

// The app shells' web views ignore a browser download, so the file goes
// through the share sheet (Save to Files, Mail, AirDrop).
async function shareFile(name: string, blob: Blob): Promise<void> {
  const { uri } = await Filesystem.writeFile({
    data: await blobToBase64(blob),
    directory: EXPORTS.directory,
    path: `${EXPORTS.path}/${name}`,
    recursive: true,
  });
  try {
    await Share.share({ files: [uri], title: name });
  } catch (error) {
    if (!(error instanceof Error && error.message === SHARE_CANCELED)) {
      throw error;
    }
  }
}

function saveInBrowser(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
}
